import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Skill } from '../types';
import { useSkillsApi } from '../apis/skills-apis';
import { Search, ChevronUp, Plus, Eye, Trash2, Loader2 } from 'lucide-react';
import _ from 'lodash';

import { Button } from "../components/ui/Button";
import { Input } from "../components/ui/Input";
import { Card, CardContent } from "../components/ui/Card";
import { Spinner } from "../components/ui/Spinner";
import { ConfirmationDialog } from '../components/ConfirmationDialog';
import { useToast } from "../components/ui/Toast";

const ITEMS_PER_PAGE = 20;

export const SkillsList: React.FC = () => {
  const navigate = useNavigate();
  const { addToast } = useToast();
  const skillsApi = useSkillsApi();
  const containerRef = useRef<HTMLDivElement>(null);
  const isMounted = useRef(false);

  const [skills, setSkills] = useState<Skill[]>([]);
  const [displayedSkills, setDisplayedSkills] = useState<Skill[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<Skill[] | null>(null);
  
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [skillToDelete, setSkillToDelete] = useState<string | null>(null);
  const [isDeletingId, setIsDeletingId] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();

    const fetchInitialData = async () => {
      try {
        setLoading(true);
        const getAllSkills = false;
        const fetchedSkills = await skillsApi.getSkills(getAllSkills);
        
        if (isMounted.current) {
          setSkills(fetchedSkills);
          setTotalCount(fetchedSkills.length);
          setDisplayedSkills(fetchedSkills.slice(0, ITEMS_PER_PAGE));
          setHasMore(fetchedSkills.length > ITEMS_PER_PAGE);
        }
      } catch (error: unknown) {
        if (isMounted.current) {
          if (error instanceof Error && error.name !== 'AbortError') {
            console.error('Error fetching skills:', error);
            addToast({
              title: "Error",
              description: "Failed to retrieve skills. Please try again.",
              variant: "destructive",
            });
          }
        }
      } finally {
        if (isMounted.current) {
          setLoading(false);
        }
      }
    };
  
    isMounted.current = true;
    fetchInitialData();
  
    return () => {
      isMounted.current = false;
      controller.abort();
    };
  }, []);

  const loadMoreSkills = useCallback(() => {
    if (searchTerm || loadingMore || !hasMore) return;

    setLoadingMore(true);
    const startIndex = displayedSkills.length;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    
    if (startIndex >= skills.length) {
      setHasMore(false);
      setLoadingMore(false);
      return;
    }

    const newItems = skills.slice(startIndex, endIndex);
    setDisplayedSkills(prev => [...prev, ...newItems]);
    
    if (endIndex >= skills.length) {
      setHasMore(false);
    }

    setLoadingMore(false);
  }, [searchTerm, loadingMore, hasMore, skills, displayedSkills.length]);

  useEffect(() => {
    const options = {
      root: null,
      rootMargin: '100px',
      threshold: 0.1
    };

    const observer = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (entry.isIntersecting && hasMore && !loadingMore && !searchTerm) {
        loadMoreSkills();
      }
    }, options);

    const loader = document.querySelector('#scroll-loader');
    if (loader) {
      observer.observe(loader);
    }

    return () => observer.disconnect();
  }, [loadMoreSkills, hasMore, loadingMore, searchTerm]);

  const handleSearch = _.debounce((term: string) => {
    setSearchTerm(term);
    if (term) {
      const results = skills.filter(skill =>
        skill.skillName.toLowerCase().includes(term.toLowerCase()) ||
        skill.skillDescription?.toLowerCase().includes(term.toLowerCase()) ||
        skill.skillTags?.some(tag => tag.toLowerCase().includes(term.toLowerCase()))
      );
      setSearchResults(results);
    } else {
      setSearchResults(null);
      // updateDisplayedSkills(1, skills);
      setDisplayedSkills(skills.slice(0, ITEMS_PER_PAGE));
      setHasMore(skills.length > ITEMS_PER_PAGE);
    }
  }, 300);

  const handleViewSkill = (skillId: string) => {
    navigate(`/marketplace/skills/edit/${skillId}`);
  };

  const handleDeleteClick = (id: string) => {
    setSkillToDelete(id);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirmed = async () => {
    if (!skillToDelete) return;

    setIsDeletingId(skillToDelete);
    try {
      await skillsApi.deleteSkill(skillToDelete);
      addToast({
        title: "Success",
        description: "Skill successfully deleted",
        variant: "default",
      });
      
      try {
        const getAllSkills = false;
        const fetchedSkills = await skillsApi.getSkills(getAllSkills);
        setSkills(fetchedSkills);
        setTotalCount(fetchedSkills.length);
        setDisplayedSkills(fetchedSkills.slice(0, ITEMS_PER_PAGE));
        setHasMore(fetchedSkills.length > ITEMS_PER_PAGE);
      } catch (error) {
        console.error('Error refreshing skills:', error);
      }
    } catch (error) {
      addToast({
        title: "Error",
        description: "Failed to delete skill. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsDeletingId(null);
      setDeleteDialogOpen(false);
      setSkillToDelete(null);
    }
  };

  useEffect(() => {
    const handleScroll = _.throttle(() => {
      setShowScrollTop(window.scrollY > 400);
    }, 200);

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const currentSkills = searchResults || displayedSkills;
  const currentTotal = searchResults ? searchResults.length : totalCount;

  return (
    <div className="container mx-auto p-4" ref={containerRef}>
      <h1 className="text-2xl mb-4">
        Skills Management
      </h1>

      <div className="mb-4">
        <div className="relative">
          <Search className="absolute left-3 top-2.5 text-muted-foreground" size={20} />
          <Input
            type="text"
            placeholder="Search skills..."
            className="w-full pl-10"
            onChange={(e) => handleSearch(e.target.value)}
          />
        </div>
      </div>

      <Button
        variant="default"
        className="mb-4 bg-green-500 hover:bg-green-600 text-white"
        onClick={() => navigate('/marketplace/skills/add')}
      >
        <Plus className="mr-2 h-4 w-4" />
        Add New Skill
      </Button>

      <div className="text-sm text-muted-foreground mb-4">
        {searchTerm ? (
          `Found ${currentSkills.length || 0} results for "${searchTerm}"`
        ) : (
          `Showing ${currentSkills.length} of ${currentTotal} skills`
        )}
      </div>
      <div className="grid gap-4">
        {currentSkills.map((skill) => (
          <Card key={skill.skillId} className="hover:shadow-md transition-shadow">
            <CardContent className="p-4">
              <div className="flex justify-between items-start">
                <div className="flex-grow">
                  <h2 className="text-xl font-semibold">{skill.skillName}</h2>
                  <p className="text-muted-foreground mt-2">{skill.skillDescription}</p>
                  {skill.skillTags && skill.skillTags.length > 0 && (
                    <div className="relative">
                      <div className="flex flex-wrap gap-1 mt-2">
                        {skill.skillTags.slice(0, 3).map((tag, index) => (
                          <span
                            key={index}
                            className="inline-flex items-center rounded-md bg-muted px-2 py-1 text-xs font-medium ring-1 ring-inset ring-muted-foreground/20"
                          >
                            {tag}
                          </span>
                        ))}
                        {skill.skillTags.length > 3 && (
                          <div className="relative inline-block group">
                            <span className="inline-flex items-center rounded-md bg-muted px-2 py-1 text-xs font-medium ring-1 ring-inset ring-muted-foreground/20 cursor-pointer">
                              +{skill.skillTags.length - 3}
                            </span>
                            <div className="absolute left-0 mt-2 p-2 bg-white border border-gray-200 rounded-md shadow-lg z-10 hidden group-hover:block">
                              {skill.skillTags.slice(3).map((tag, index) => (
                                <span
                                  key={index}
                                  className="block text-xs text-gray-700"
                                >
                                  {tag}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                <div className="flex gap-2 ml-4">
                  <Button
                    variant="secondary"
                    onClick={() => handleViewSkill(skill.skillId || '')}
                    className="bg-blue-100 hover:bg-blue-200 text-blue-600"
                    disabled={isDeletingId === skill.skillId}
                  >
                    <Eye className="mr-2 h-4 w-4" />
                    View
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={() => handleDeleteClick(skill.skillId || '')}
                    className="bg-red-500 hover:bg-red-600 text-white"
                    disabled={isDeletingId === skill.skillId}
                  >
                    {isDeletingId === skill.skillId ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Deleting...
                      </>
                    ) : (
                      <>
                        <Trash2 className="mr-2 h-4 w-4" />
                        Delete
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {!searchTerm && hasMore && (
        <div id="scroll-loader" className="h-10 w-full" />
      )}

      {loading ? (
        <div className="flex flex-col items-center my-8 gap-2">
          <Spinner className="border-blue-500" />
          <p className="text-center text-muted-foreground">Loading Skills...</p>
        </div>
      ) : loadingMore ? (
        <div className="flex justify-center my-4">
          <Spinner className="border-blue-500" />
        </div>
      ) : (!loading && currentSkills.length === 0) && (
        <Card className="p-8">
          <p className="text-center text-muted-foreground">
            {searchTerm ? 'No skills found matching your search.' : 'No skills found. Create a new skill to get started.'}
          </p>
        </Card>
      )}

      <ConfirmationDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onConfirm={handleDeleteConfirmed}
        title="Delete Skill"
        description="This will permanently delete the skill from the system. This action cannot be undone."
        confirmLabel={isDeletingId ? "Deleting..." : "Delete"}
        cancelLabel="Cancel"
        disabled={Boolean(isDeletingId)}
        requireVerification={true}
        verificationWord="DELETE"
      />

      {showScrollTop && (
        <Button
          variant="secondary"
          size="icon"
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="fixed bottom-8 right-8 rounded-full shadow-lg bg-blue-500 hover:bg-blue-600 text-white"
        >
          <ChevronUp size={24} />
        </Button>
      )}
    </div>
  );
};

export default SkillsList;